import { createSelector } from 'reselect';
import Mentor from 'models/Mentor';
import mentorSelectors from 'store/modules/mentor/selectors';
import { resolveCurrentManager } from 'features/manager/infrastructure/mappers/managerMapper';
import { ManagerCapabilities } from 'features/manager/domain/ManagerCapabilities';

const getState = state => state.user;

const getUser = createSelector([getState], state => state.get('data'));

const getUserUuid = createSelector([getUser], user => user.get('uuid'));

const isAuthenticated = createSelector([getState], state => state.get('isAuthenticated'));

const isMember = createSelector([getUser, isAuthenticated], (user, authenticated) =>
  Boolean(authenticated && user.get('member'))
);

const getManagerProfile = createSelector([getUser], user => user.get('manager'));

const selectManagerCapabilities = createSelector(
  [mentorSelectors.getMentor, getManagerProfile],
  (mentor, managerProfile) =>
    ManagerCapabilities.fromManager(resolveCurrentManager(mentor, managerProfile))
);

// The manager headers read legacy Mentor fields, so a manager-only account shows its v2 profile
// through a Mentor record.
const getManagerDisplayUser = createSelector(
  [mentorSelectors.getMentor, getManagerProfile],
  (mentor, managerProfile) => {
    if (mentor?.is_admin || !managerProfile) return mentor;

    return new Mentor({
      first_name: managerProfile.firstName,
      last_name: managerProfile.lastName,
      email: managerProfile.email,
    });
  }
);

const selectActorAnalyticsType = createSelector(
  [selectManagerCapabilities, isMember],
  (capabilities, hasMember) => {
    if (capabilities.actorType === 'manager') return 'Admin';
    return hasMember ? 'Entrepreneur' : 'Mentor';
  }
);

const isMentor = createSelector([getUser, isAuthenticated], (user, authenticated) =>
  Boolean(authenticated && user.get('mentor'))
);

const getUserLinks = createSelector([getState], state => state.get('linkData'));

const fetchingUser = createSelector([getState], state => state.get('isFetching'));

const userIsAccessible = createSelector([getState], state => state.get('userIsAccessible'));

const getHighContrastMode = createSelector([getState], state => state.get('highContrastMode'));

const isError = createSelector([getState], state => state.get('isError'));

const isPendingActivation = createSelector(
  [getState, isError, fetchingUser, getUser],
  (state, error, isFetching, user) => !error && !isFetching && !!user.id && !user.isActive()
);

const getErrorMessage = createSelector([getState], state => state.get('errorMessage'));

const getForgotPasswordRequested = createSelector([getState], state =>
  state.get('forgotPasswordRequested')
);

const isEmailVerified = createSelector([getUser, isAuthenticated], (user, authenticated) =>
  Boolean(authenticated && user.isEmailVerified())
);

export default {
  getUser,
  getUserUuid,
  isAuthenticated,
  isMember,
  isMentor,
  isEmailVerified,
  isPendingActivation,
  fetchingUser,
  userIsAccessible,
  getHighContrastMode,
  isError,
  getErrorMessage,
  getForgotPasswordRequested,
  getUserLinks,
  getManagerProfile,
  getManagerDisplayUser,
  selectManagerCapabilities,
  selectActorAnalyticsType,
};
